import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { Image } from '@educator/components/Image'
import { IconDown } from '@mc/educator-design-system'
import { useTranslation } from '@mc/i18n'
import {
  TrackClick,
  nameToTrackingAction,
} from '../../../../tracking/educatorTracking'
import { paths } from '../../../../utils/paths'

const SCROLL_THRESHOLD = 100

interface SubjectNavItem {
  slug: string
  name: string
  comingSoon?: boolean
}

interface SubjectPageNavProps {
  subjects: SubjectNavItem[]
  currentSlug: string
  ctaHref: string
  ctaLabel: string
  secondaryCtaLabel?: string
  secondaryCtaHref?: string
  isPrimaryLogIn?: boolean
}

const NavLogo = ({
  src,
  width,
  className = '',
}: {
  src: string
  width: number
  className?: string
}) => {
  const { t } = useTranslation('@mc/educator')
  return (
    <TrackClick
      event='educatorSignClick'
      action='masterclass executive'
      location='top navigation'
    >
      <Link
        href={paths.programDetails}
        aria-label={t('subjectLandingPage.subjectNav.logoLabel')}
        className={`flex shrink-0 items-center no-underline ${className}`}
      >
        <Image src={src} alt='' width={width} height={32} />
      </Link>
    </TrackClick>
  )
}

export const SubjectPageNav = ({
  subjects,
  currentSlug,
  ctaHref,
  ctaLabel,
  secondaryCtaLabel,
  secondaryCtaHref,
  isPrimaryLogIn = false,
}: SubjectPageNavProps) => {
  const { t } = useTranslation('@mc/educator')
  const [isOpen, setIsOpen] = useState(false)
  const [isHidden, setIsHidden] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const lastScrollY = useRef(0)
  const currentSubject = subjects.find((s) => s.slug === currentSlug)

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY
      if (currentY > lastScrollY.current && currentY > SCROLL_THRESHOLD) {
        setIsHidden(true)
        setIsOpen(false)
      } else if (currentY < lastScrollY.current) {
        setIsHidden(false)
      }
      lastScrollY.current = currentY
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  return (
    <header
      className={`fixed md:top-[40px] right-0 left-0 z-50 hidden md:flex justify-center px-5 transition-[opacity,transform] duration-300 ease-in-out ${
        isHidden
          ? '-translate-y-[calc(100%+30px)] pointer-events-none opacity-0'
          : ''
      }`}
    >
      <nav
        aria-label={t('subjectLandingPage.subjectNav.navLabel')}
        className='w-full max-w-[960px] bg-black'
      >
        <div className='hidden items-center justify-between px-5 py-3 md:flex'>
          <div className='flex items-center gap-[40px]'>
            <NavLogo
              src='https://static.masterclass.com/masterclass-executive-mobile-logo.svg'
              width={108}
              className='lg:hidden'
            />
            <NavLogo
              src='https://static.masterclass.com/masterclass-executive-logo.svg'
              width={262}
              className='hidden lg:flex'
            />

            <div ref={dropdownRef} className='relative w-[360px]'>
              <button
                type='button'
                aria-expanded={isOpen}
                aria-controls='subject-nav-dropdown'
                onClick={() => setIsOpen((v) => !v)}
                className={`flex w-full cursor-pointer items-center justify-between gap-1 lg:gap-8 border px-5 py-2 text-body-2 lg:text-body-1 text-edu-fg-primary transition-colors ${
                  isOpen
                    ? 'border-b-0 border-white'
                    : 'border-edu-stroke-medium hover:border-white'
                }`}
              >
                <span>
                  {currentSubject?.name ??
                    t('subjectLandingPage.subjectNav.subjectsFallback')}
                </span>
                <IconDown
                  className={`size-8! shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isOpen && (
                <ul
                  id='subject-nav-dropdown'
                  className='absolute top-full w-full border border-t-0 border-white bg-black pb-2'
                >
                  {subjects.map((subject) => (
                    <li key={subject.slug}>
                      <div
                        aria-hidden='true'
                        className='mx-5 border-t border-edu-stroke-medium'
                      />
                      {subject.comingSoon ? (
                        <span className='block px-5 py-2 text-body-2 lg:text-body-1 text-edu-fg-primary opacity-20 cursor-not-allowed'>
                          {subject.name}
                        </span>
                      ) : (
                        <TrackClick
                          event='educatorSignClick'
                          action={nameToTrackingAction(subject.name)}
                          location='top navigation'
                        >
                          <Link
                            href={`${paths.subjects}/${subject.slug}`}
                            className='block px-5 py-2 text-body-2 lg:text-body-1 text-edu-fg-primary no-underline transition-colors hover:bg-edu-button-hover'
                            onClick={() => setIsOpen(false)}
                          >
                            {subject.name}
                          </Link>
                        </TrackClick>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className='flex items-center gap-4 shrink-0'>
            {secondaryCtaLabel && secondaryCtaHref && (
              <TrackClick
                event='educatorSignClick'
                action={secondaryCtaLabel.toLowerCase()}
                location='top navigation'
              >
                {isPrimaryLogIn ? (
                  <TrackClick
                    event='educatorPurchaseClick'
                    cta_location='top navigation'
                    cta={secondaryCtaLabel}
                  >
                    <Link
                      href={secondaryCtaHref}
                      className='text-body-1 font-medium text-edu-fg-primary no-underline transition-colors hover:opacity-70'
                    >
                      {secondaryCtaLabel}
                    </Link>
                  </TrackClick>
                ) : (
                  <Link
                    href={secondaryCtaHref}
                    className='text-body-1 font-medium text-edu-fg-primary no-underline transition-colors hover:opacity-70'
                  >
                    {secondaryCtaLabel}
                  </Link>
                )}
              </TrackClick>
            )}
            <TrackClick
              event='educatorSignClick'
              action={ctaLabel.toLowerCase()}
              location='top navigation'
            >
              {isPrimaryLogIn ? (
                <Link
                  href={ctaHref}
                  className='bg-edu-accent-indigo-medium px-5 py-4 text-center text-body-1 leading-none font-medium transition-colors hover:bg-edu-accent-indigo-dark text-edu-fg-primary no-underline'
                >
                  {ctaLabel}
                </Link>
              ) : (
                <TrackClick
                  event='educatorPurchaseClick'
                  cta_location='top navigation'
                  cta={ctaLabel}
                >
                  <Link
                    href={ctaHref}
                    className='bg-edu-accent-indigo-medium px-5 py-4 text-center text-body-1 leading-none font-medium transition-colors hover:bg-edu-accent-indigo-dark text-edu-fg-primary no-underline'
                  >
                    {ctaLabel}
                  </Link>
                </TrackClick>
              )}
            </TrackClick>
          </div>
        </div>
      </nav>
    </header>
  )
}
